import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Search, MapPin, CreditCard, CalendarCheck, ArrowRight, Star, Tent, Caravan, TruckIcon, Quote, Award, Crown } from "lucide-react";
import api from "@/lib/api";

const HERO_BG_DEFAULT = "/images/hero.jpg";
const CTA_IMG = "/images/hero.jpg";
const DEFAULT_IMAGES = [
  "/images/hero.jpg",
];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TERRAIN_LABELS = { rv: "VR", tent: "Tente", van: "Van", all: "Tous" };

function getImageUrl(space, idx) {
  const img = space.photos?.[0] || space.image_url;
  if (!img) return DEFAULT_IMAGES[idx % DEFAULT_IMAGES.length];
  if (img.startsWith("/api/")) return `${BACKEND_URL}${img}`;
  return img;
}

export default function HomePage() {
  const [search, setSearch] = useState("");
  const [spaces, setSpaces] = useState([]);
  const [settings, setSettings] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/spaces").then((r) => setSpaces(r.data.slice(0, 6))).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);
  const handleSearch = (e) => { e.preventDefault(); navigate(`/spaces?search=${encodeURIComponent(search)}`); };

  const heroImg = settings?.hero_image ? (settings.hero_image.startsWith("/api/") ? `${BACKEND_URL}${settings.hero_image}` : settings.hero_image) : HERO_BG_DEFAULT;
  const heroTitle = settings?.hero_title || "Campez sur des terrains privés uniques";
  const heroSubtitle = settings?.hero_subtitle || "Découvrez des emplacements VR, tente et van chez des propriétaires accueillants partout au Québec.";
  const searchBtnText = settings?.search_button_text || "Explorer";
  const hostCtaImg = settings?.host_cta_image ? (settings.host_cta_image.startsWith("/api/") ? `${BACKEND_URL}${settings.host_cta_image}` : settings.host_cta_image) : CTA_IMG;
  const hostCtaTitle = settings?.host_cta_title || "Louez votre terrain et gagnez de l'argent";
  const hostCtaDesc = settings?.host_cta_desc || "Transformez votre lopin de terre en source de revenus. Accueillez des campeurs passionnes, partagez votre coin de paradis et gagnez jusqu'a plusieurs milliers de dollars par saison - tout en restant maitre de votre calendrier et de vos règles.";
  const hostCtaBtn = settings?.host_cta_button || "Publier mon terrain";
  const steps = [
    { icon: Search, title: settings?.step1_title || "Explorez", desc: settings?.step1_desc || "Parcourez des terrains privés uniques pour VR, tente ou van amenage." },
    { icon: CalendarCheck, title: settings?.step2_title || "Reservez", desc: settings?.step2_desc || "Choisissez vos dates d'arrivée et de départ, puis reservez facilement." },
    { icon: CreditCard, title: settings?.step3_title || "Campez", desc: settings?.step3_desc || "Payez en ligne de manière sécurisée et profitez de votre séjour nature." },
  ];

  return (
    <div>
      <section className="relative h-[75vh] min-h-[500px] flex items-center justify-center bg-cover bg-center" style={{ backgroundImage: `url(${heroImg})` }}>
        <div className="absolute inset-0 bg-black/45" />
        <div className="relative z-10 text-center px-4 max-w-3xl mx-auto animate-fade-in">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white mb-4" data-testid="hero-title">
            {heroTitle}
          </h1>
          <p className="text-base sm:text-lg text-white/80 mb-8 max-w-xl mx-auto">
            {heroSubtitle}
          </p>
          <form onSubmit={handleSearch} className="flex gap-2 max-w-lg mx-auto" data-testid="hero-search-form">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Région, ville, type de terrain..." className="pl-10 h-12 bg-white border-0 rounded-xl text-zinc-950" data-testid="hero-search-input" />
            </div>
            <Button type="submit" className="h-12 px-6 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl" data-testid="hero-search-btn">{searchBtnText}</Button>
          </form>
        </div>
      </section>

      <section className="py-16 px-4 max-w-7xl mx-auto">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950 mb-10 text-left">{settings?.how_title || "Comment ca marche"}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {steps.map((step, i) => (
            <Card key={i} className="p-6 border border-zinc-200 rounded-2xl bg-white hover:shadow-md transition-shadow" data-testid={`how-step-${i}`}>
              <div className="h-12 w-12 rounded-xl bg-emerald-600/10 flex items-center justify-center mb-4"><step.icon className="h-6 w-6 text-emerald-600" /></div>
              <h3 className="font-heading text-lg font-bold text-zinc-950 mb-2">{step.title}</h3>
              <p className="text-sm text-zinc-500">{step.desc}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Host CTA - Invitation propriétaires */}
      <section className="py-12 px-4 bg-gradient-to-br from-emerald-50 via-white to-amber-50/40" data-testid="host-cta-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="text-left order-2 lg:order-1">
              <Badge className="mb-3 bg-emerald-100 text-emerald-800 border-0 px-3 py-1">Propriétaires de terrain</Badge>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tighter text-zinc-950 mb-3 leading-tight">{hostCtaTitle}</h2>
              <p className="text-zinc-600 text-sm leading-relaxed mb-4 whitespace-pre-line">{hostCtaDesc}</p>
              <div className="flex flex-wrap gap-3 mb-5">
                <div className="flex items-center gap-2 text-xs text-zinc-700"><div className="h-7 w-7 rounded-full bg-emerald-600/10 flex items-center justify-center"><CalendarCheck className="h-3.5 w-3.5 text-emerald-600" /></div><span>Vous choisissez vos dates</span></div>
                <div className="flex items-center gap-2 text-xs text-zinc-700"><div className="h-7 w-7 rounded-full bg-emerald-600/10 flex items-center justify-center"><CreditCard className="h-3.5 w-3.5 text-emerald-600" /></div><span>Paiements sécurisés</span></div>
                <div className="flex items-center gap-2 text-xs text-zinc-700"><div className="h-7 w-7 rounded-full bg-amber-500/10 flex items-center justify-center"><Crown className="h-3.5 w-3.5 text-amber-600" /></div><span>Abonnement requis</span></div>
              </div>
              <Link to="/create-space" data-testid="cta-create-space">
                <Button className="bg-emerald-700 text-white hover:bg-emerald-800 rounded-xl px-6 h-11 text-sm">
                  {hostCtaBtn} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/superstar" className="ml-3 text-sm font-medium text-amber-700 hover:text-amber-800 inline-flex items-center gap-1 underline-offset-4 hover:underline" data-testid="learn-superstar-link">
                <Award className="h-3.5 w-3.5" /> Découvrir le programme Superstar
              </Link>
            </div>
            <div className="order-1 lg:order-2">
              <div className="relative max-w-md mx-auto lg:ml-auto">
                <div className="absolute -inset-3 bg-emerald-600/10 rounded-3xl blur-2xl" />
                <div className="relative rounded-2xl overflow-hidden aspect-[4/3] shadow-lg">
                  <img src={hostCtaImg} alt={hostCtaTitle} className="w-full h-full object-cover" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {spaces.length > 0 && (
        <section className="py-16 px-4 max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950">Terrains populaires</h2>
            <Link to="/spaces" className="text-sm font-medium text-emerald-600 hover:underline" data-testid="view-all-spaces">Voir tout</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {spaces.map((space, i) => (
              <Link to={`/spaces/${space.id}`} key={space.id} data-testid={`featured-space-${i}`}>
                <Card className="border border-zinc-200 rounded-2xl bg-white overflow-hidden hover:shadow-md transition-all duration-200 hover:-translate-y-[2px]">
                  <div className="aspect-video overflow-hidden relative">
                    <img src={getImageUrl(space, i)} alt={space.title} className="w-full h-full object-cover" />
                    <Badge className="absolute top-3 right-3 bg-white/90 text-zinc-950 backdrop-blur-sm border-0">{TERRAIN_LABELS[space.terrain_type] || "VR"}</Badge>
                    {space.host_is_superstar && (
                      <Badge className="absolute top-3 left-3 bg-gradient-to-r from-amber-400 to-amber-500 text-white border-0 gap-1 shadow-md" data-testid={`home-superstar-${i}`}>
                        <Award className="h-3 w-3" /> Superstar
                      </Badge>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-heading font-bold text-zinc-950 truncate">{space.title}</h3>
                      {space.is_free ? <span className="text-amber-600 font-bold text-sm whitespace-nowrap">Gratuit</span> : <span className="text-emerald-600 font-bold text-sm whitespace-nowrap">{space.price_per_night?.toFixed(2)} $/nuit</span>}
                    </div>
                    <p className="text-sm text-zinc-500 flex items-center gap-1"><MapPin className="h-3 w-3" /> {space.city}{space.region ? `, ${space.region}` : ""}</p>
                    {space.avg_rating > 0 && (
                      <div className="flex items-center gap-1 mt-2 text-sm"><Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /><span className="font-medium text-zinc-700">{space.avg_rating}</span><span className="text-zinc-400">({space.review_count})</span></div>
                    )}
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Testimonials */}
      {settings?.testimonials?.filter(t => t.visible !== false && t.comment).length > 0 && (
        <section className="py-16 px-4 bg-zinc-950">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white mb-10 text-center">
              {settings?.testimonials_title || "Ce que nos campeurs disent"}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {settings.testimonials.filter(t => t.visible !== false && t.comment).map((t, i) => (
                <Card key={i} className="bg-zinc-900 border-zinc-800 rounded-2xl p-6 relative" data-testid={`testimonial-display-${i}`}>
                  <Quote className="h-8 w-8 text-emerald-600/30 absolute top-4 right-4" />
                  <div className="flex gap-0.5 mb-3">
                    {[1,2,3,4,5].map(s => (
                      <Star key={s} className={`h-4 w-4 ${s <= (t.rating || 5) ? "fill-amber-400 text-amber-400" : "text-zinc-700"}`} />
                    ))}
                  </div>
                  <p className="text-zinc-300 text-sm leading-relaxed mb-4">"{t.comment}"</p>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-emerald-600/20 flex items-center justify-center text-sm font-bold text-emerald-400">
                      {t.name?.charAt(0)?.toUpperCase() || "?"}
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">{t.name}</p>
                      {t.role && <p className="text-zinc-500 text-xs">{t.role}</p>}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      <footer className="border-t border-zinc-200 py-8 px-4 mt-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-sm text-zinc-500">
          <span className="font-heading font-bold text-zinc-950">{settings?.app_name || ""}</span>
          <span>Camping sur terrains privés - simplifie</span>
        </div>
      </footer>
    </div>
  );
}
